import React, { useMemo, useState } from "react";
import { Box, Container } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { WizardStepper } from "../../shared/ui";
import Grid from "@mui/material/Grid";
import { useParams } from "react-router-dom";
import { GenerateReportPageSkeleton } from "../../components/skeletons/GenerateReportPageSkeleton";
import { stationTemplatesQuery } from "../../features/templates/api/templateQueries";
import { useOrgId } from "../../shared/auth/orgContext";
import { NarrativeTemplate } from "../../shared/interfaces/ReportGenReport";
import { useJobStore } from "../../store/jobStore";
import { useToast } from "../../shared/toast";
import { useVideoData, useReportGeneration } from "./hooks";
import {
  VideoEvidenceSection,
  ReportConfigSection,
  GenerateReportError,
} from "./components";
import { usePageHeader } from "../../context/LayoutContext";
import BoltIcon from "@mui/icons-material/Bolt";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

const GenerateReportPage: React.FC = () => {
  const { evidenceID } = useParams();
  const isMaxJobsReached = useJobStore((state) => state.isMaxJobsReached());
  const organizationId = useOrgId();
  // Usually served from the cache MainComponent's eager observer warmed at
  // login; `?? null` keeps the old null-while-loading render path.
  const { data: stationTemplates } = useQuery({
    ...stationTemplatesQuery(organizationId ?? ""),
    enabled: !!organizationId,
  });
  const customReportTypes = stationTemplates ?? null;
  const toast = useToast();

  // default report type
  const generalReportType: NarrativeTemplate = useMemo(
    () => ({
      template: { name: "general", content: "", stationId: "", templateId: "default", arguments: {} },
      examples: [],
    }),
    []
  );
  const [reportType, setReportType] = useState<NarrativeTemplate>(generalReportType);

  const {
    videoData,
    videoDataList,
    currentPage,
    viewedVideos,
    loading,
    viewedCount,
    totalVideos,
    generateDisabled,
    handlePageChange,
    handleNextVideo,
    handlePrevVideo,
  } = useVideoData(evidenceID, toast.error);

  const { isGenerating, handleSubmitClick } = useReportGeneration(
    evidenceID,
    reportType,
    videoDataList,
    isMaxJobsReached,
    toast.error
  );

  const handleReportTypeChange = (value: NarrativeTemplate) => {
    setReportType(value);
  };

  // Set page header in app bar
  usePageHeader({
    title: "Generate Report",
    subtitle: "Review video evidence and create incident reports",
    icon: <BoltIcon />,
    chip: { label: `${videoDataList.length} ${videoDataList.length === 1 ? 'Video' : 'Videos'}`, icon: <PlayArrowIcon /> },
  });

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <GenerateReportPageSkeleton />
      </Box>
    );
  }

  if (!videoData) {
    return <GenerateReportError onRetry={() => window.location.reload()} />;
  }

  return (
    <>
      <Container sx={{ minWidth: { xs: "200px", sm: "450px" }, overflow: "auto" }}>
        <WizardStepper
          activeStep={1}
          // The evidence selection itself is in-memory and does not survive
          // the round trip; the URL still carries these videos forward.
          back={{ label: "Evidence selection", to: "/video-selection" }}
          sx={{ mt: 2 }}
        />
        <Grid container spacing={3} direction="column" sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
          <Grid size={{ xs: 12 }}>
            <Grid container spacing={2} sx={{ width: "100%" }}>
              <Grid size={{ xs: 12, md: 8 }}>
                <VideoEvidenceSection
                  videoData={videoData as NonNullable<typeof videoData>}
                  videoDataListLength={videoDataList.length}
                  currentPage={currentPage}
                  viewedVideos={viewedVideos}
                  handlePageChange={handlePageChange}
                  handlePrevVideo={handlePrevVideo}
                  handleNextVideo={handleNextVideo}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <ReportConfigSection
                  reportType={reportType}
                  generalReportType={generalReportType}
                  customReportTypes={customReportTypes ?? undefined}
                  onReportTypeChange={handleReportTypeChange}
                  onGenerate={handleSubmitClick}
                  isGenerating={isGenerating}
                  isMaxJobsReached={isMaxJobsReached}
                  generateDisabled={generateDisabled}
                  viewedCount={viewedCount}
                  totalVideos={totalVideos}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default GenerateReportPage;
export { GenerateReportPage };
